import { Eye, Bookmark, Heart, Share2, Monitor } from "lucide-react";
import { AnimatedCounter } from "./AnimatedCounter";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";

interface HomeEngagement {
  address: string;
  city: string;
  state: string;
  views_count: number;
  saves_count: number;
  portal_views: number;
  portal_saves: number;
  report_views: number;
  image_url: string;
  price: number | null;
}

interface SpotlightDigitalFootprintProps {
  portalViews: number;
  portalSaves: number;
  totalViews: number;
  totalSaves: number;
  homes: HomeEngagement[];
  primaryColor?: string | null;
  viewSources?: Record<string, number> | null;
}

const SOCIAL_PLATFORMS = [
  { key: "facebook", name: "Facebook", color: "#1877F2" },
  { key: "instagram", name: "Instagram", color: "#E4405F" },
  { key: "youtube", name: "YouTube", color: "#FF0000" },
  { key: "linkedin", name: "LinkedIn", color: "#0A66C2" },
  { key: "tiktok", name: "TikTok", color: "#000000" },
  { key: "twitter", name: "X", color: "#000000" },
] as const;

const PORTAL_PLATFORMS = [
  { key: "zillow", name: "Zillow", color: "#006AFF" },
  { key: "realtor", name: "Realtor.com", color: "#D92228" },
  { key: "homes", name: "Homes.com", color: "#ff850f" },
  { key: "redfin", name: "Redfin", color: "#de3341" },
  { key: "trulia", name: "Trulia", color: "#02ADBA" },
  { key: "mls", name: "Local MLS", color: "#1b6b3a" },
] as const;

function formatNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function formatCompactPrice(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value.toLocaleString()}`;
}

export function SpotlightDigitalFootprint({
  portalViews,
  portalSaves,
  totalViews,
  totalSaves,
  homes,
  primaryColor,
  viewSources,
}: SpotlightDigitalFootprintProps) {
  const { ref: scrollRef, progress } = useScrollProgress();
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.15);

  const hasData = totalViews > 0 || totalSaves > 0;
  if (!hasData) return null;

  const accentColor = primaryColor ?? "#3B82F6";
  const parallaxY = (progress - 0.5) * -20;

  const portalSaveRate = portalViews > 0 ? ((portalSaves / portalViews) * 100).toFixed(1) : "0";
  const overallSaveRate = totalViews > 0 ? ((totalSaves / totalViews) * 100).toFixed(1) : "0";

  // Top listings by portal views
  const topPortalListings = [...homes]
    .filter((h) => h.portal_views > 0)
    .sort((a, b) => b.portal_views - a.portal_views)
    .slice(0, 5);

  const hasPortalData = portalViews > 0 || portalSaves > 0;

  // Per-platform breakdown from view_sources (aggregated from social_media_posts + portal columns)
  const hasViewSources = viewSources && Object.keys(viewSources).length > 0;

  // Social platforms: engagement data from social_media_posts
  const socialEntries = hasViewSources
    ? SOCIAL_PLATFORMS
        .map((p) => ({ ...p, views: viewSources[p.key] ?? 0 }))
        .filter((p) => p.views > 0)
        .sort((a, b) => b.views - a.views)
    : [];

  const maxSocialViews = socialEntries.length > 0
    ? Math.max(...socialEntries.map((e) => e.views), 1)
    : 1;

  // Portal platforms: view data from norths table (zillow/realtor columns)
  const portalEntries = hasViewSources
    ? PORTAL_PLATFORMS
        .map((p) => ({ ...p, views: viewSources[p.key] ?? 0 }))
        .filter((p) => p.views > 0)
    : [];

  const maxPortalViews = portalEntries.length > 0
    ? Math.max(...portalEntries.map((e) => e.views), 1)
    : 1;

  // Total social engagement = sum of all social platform entries
  const totalSocialEngagement = socialEntries.reduce((sum, e) => sum + e.views, 0);

  // Determine what the left column shows
  const showPerPlatform = socialEntries.length > 0;
  const showLeftColumn = showPerPlatform;

  return (
    <section
      ref={scrollRef}
      className="w-full py-24 md:py-32 px-6"
      style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)" }}
    >
      <div
        ref={animRef}
        className="max-w-7xl mx-auto will-change-transform"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-4">
          Digital Footprint
        </p>
        <p className={`text-3xl md:text-4xl font-bold text-slate-900 mb-4 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}>
          {formatNumber(totalViews + totalSocialEngagement)} total views across all channels
        </p>
        <p className={`text-base md:text-lg text-slate-500 leading-relaxed mb-12 transition-all duration-700 delay-100 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}>
          {totalViews + totalSocialEngagement >= 10000
            ? `Every view represents a potential buyer discovering your listings. With ${formatNumber(totalViews + totalSocialEngagement)} impressions across online portals and social media, your properties are reaching a wide and engaged audience that drives showings, offers, and ultimately, results.`
            : totalViews + totalSocialEngagement >= 1000
              ? `Your listings are generating strong online visibility. ${formatNumber(totalViews + totalSocialEngagement)} views means your properties are being seen by active buyers across multiple platforms, building momentum that translates into real-world interest and competitive offers.`
              : `Your digital presence is growing. Each view is a connection with a potential buyer, and consistent online exposure across listing portals and social channels helps your properties stand out in a competitive market.`}
        </p>

        {/* Two-column split — equal height via flex stretch */}
        <div className={`grid grid-cols-1 ${showLeftColumn && hasPortalData ? "lg:grid-cols-2" : ""} gap-10 items-stretch`}>

          {/* LEFT: Social & Direct */}
          {showLeftColumn && (
            <div
              className={`flex flex-col transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
                  <Share2 className="h-4 w-4" style={{ color: accentColor }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Social & Direct</h3>
                  <p className="text-xs text-slate-400">Engagement across social media platforms</p>
                </div>
              </div>

              <div className="flex-1 rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-6 md:p-8 space-y-6 shadow-sm">
                {/* Headline stat */}
                <div>
                  <AnimatedCounter
                    value={formatNumber(totalSocialEngagement)}
                    className="text-4xl md:text-5xl font-bold text-slate-900 block"
                    duration={2000}
                  />
                  <p className="text-sm text-slate-400 mt-1">Total Social Reach</p>
                </div>

                {/* Per-platform breakdown */}
                <div className="border-t border-slate-100 pt-5 space-y-3">
                  {socialEntries.map((entry, i) => {
                    const barWidth = (entry.views / maxSocialViews) * 100;
                    return (
                      <div key={entry.key}>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-sm text-slate-700 flex-1">{entry.name}</span>
                          <span className="text-sm font-semibold text-slate-900 tabular-nums">
                            {formatNumber(entry.views)}
                          </span>
                        </div>
                        <div className="mt-1.5 ml-[22px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: isVisible ? `${barWidth}%` : "0%",
                              backgroundColor: entry.color,
                              opacity: 0.7,
                              transitionDelay: `${600 + i * 120}ms`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* RIGHT: Online Portals (Zillow / Realtor) */}
          {hasPortalData && (
            <div
              className={`flex flex-col transition-all duration-700 ${showLeftColumn ? "delay-200" : ""} ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
            >
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
                  <Monitor className="h-4 w-4" style={{ color: accentColor }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Online Portals</h3>
                  <p className="text-xs text-slate-400">Engagement on Zillow, Realtor.com, Homes.com, Redfin, Trulia, Local MLS & more</p>
                </div>
              </div>

              <div className="flex-1 rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-6 md:p-8 space-y-6 shadow-sm flex flex-col">
                {/* Portal referrer breakdown */}
                {portalEntries.length > 0 && (
                  <div className="space-y-3">
                    {portalEntries.map((entry, i) => {
                      const barWidth = (entry.views / maxPortalViews) * 100;
                      return (
                        <div key={entry.key}>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-sm text-slate-700 flex-1">{entry.name}</span>
                            <span className="text-sm font-semibold text-slate-900 tabular-nums">
                              {formatNumber(entry.views)} views
                            </span>
                          </div>
                          <div className="mt-1.5 ml-[22px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-1000"
                              style={{
                                width: isVisible ? `${barWidth}%` : "0%",
                                backgroundColor: entry.color,
                                opacity: 0.7,
                                transitionDelay: `${600 + i * 120}ms`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Stats row — pushed to bottom */}
                <div className={`mt-auto grid grid-cols-3 gap-4 ${portalEntries.length > 0 ? "border-t border-slate-100 pt-5" : ""}`}>
                  <div>
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-full mb-2" style={{ backgroundColor: `${accentColor}15` }}>
                      <Eye className="h-4 w-4" style={{ color: accentColor }} />
                    </div>
                    <AnimatedCounter
                      value={formatNumber(portalViews)}
                      className="text-2xl md:text-3xl font-bold text-slate-900 block"
                      duration={2000}
                    />
                    <p className="text-xs text-slate-400 mt-0.5">Listing Views</p>
                  </div>
                  <div>
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-full mb-2" style={{ backgroundColor: `${accentColor}15` }}>
                      <Bookmark className="h-4 w-4" style={{ color: accentColor }} />
                    </div>
                    <AnimatedCounter
                      value={formatNumber(portalSaves)}
                      className="text-2xl md:text-3xl font-bold text-slate-900 block"
                      duration={2000}
                    />
                    <p className="text-xs text-slate-400 mt-0.5">Buyer Saves</p>
                  </div>
                  <div>
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-full mb-2" style={{ backgroundColor: `${accentColor}15` }}>
                      <Heart className="h-4 w-4" style={{ color: accentColor }} />
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-slate-900">{portalSaveRate}%</p>
                    <p className="text-xs text-slate-400 mt-0.5">Save Rate</p>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>

        {/* Top Performing Listings — full width */}
        {topPortalListings.length > 0 && (
          <div
            className={`mt-10 rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-6 md:p-8 shadow-sm transition-all duration-700 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-4">
              Top Performing Listings
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topPortalListings.map((home, i) => {
                const listingSaveRate = home.portal_views > 0
                  ? ((home.portal_saves / home.portal_views) * 100).toFixed(1)
                  : "0";
                const barWidth = topPortalListings[0].portal_views > 0
                  ? (home.portal_views / topPortalListings[0].portal_views) * 100
                  : 0;

                return (
                  <div key={i} className="p-4 rounded-xl bg-slate-50/60">
                    <div className="flex items-center gap-3">
                      <img
                        src={home.image_url}
                        alt={home.address}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{home.address}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                          {home.price && <span>{formatCompactPrice(home.price)}</span>}
                          <span>{home.city}, {home.state}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="font-semibold text-slate-900">{formatNumber(home.portal_views)} views</span>
                      <span className="text-slate-400">{formatNumber(home.portal_saves)} saves</span>
                      <span className="text-slate-400">{listingSaveRate}%</span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: isVisible ? `${barWidth}%` : "0%",
                          backgroundColor: accentColor,
                          opacity: 0.6,
                          transitionDelay: `${800 + i * 150}ms`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Overall summary bar */}
        <div
          className={`mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 rounded-2xl border border-slate-200 bg-white/40 backdrop-blur-sm p-6 shadow-sm transition-all duration-700 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{formatNumber(totalViews + totalSocialEngagement)}</p>
            <p className="text-xs text-slate-400 mt-0.5">Total Views</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{formatNumber(totalSaves)}</p>
            <p className="text-xs text-slate-400 mt-0.5">Portal Saves</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{overallSaveRate}%</p>
            <p className="text-xs text-slate-400 mt-0.5">Portal Save Rate</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{homes.length > 0 ? formatNumber(Math.round(totalViews / homes.length)) : "0"}</p>
            <p className="text-xs text-slate-400 mt-0.5">Avg Views / Listing</p>
          </div>
        </div>
      </div>
    </section>
  );
}
